import { characterPath } from '../utils/characterSlug'

// Same worker as the news proxy; its origin is already allowed by the production CSP
// (`connect-src` in public/index.html).
const ARMORY_WORKER_URL = 'https://wowhead-rss-proxy.andrey-ilutsa.workers.dev'
// Mirrors ARMORY_PARSER_VERSION in src/services/cloudFlareWorker.js. Cached entries written by an
// older parser are ignored instead of being rendered against a changed shape.
const ARMORY_SCHEMA_VERSION = '2026-08-10-armory-v2-render'
const ARMORY_SECTIONS = ['profile', 'reputation', 'achievements']
const ARMORY_STORAGE_KEY = 'rio-armory-cache-v1'
const ARMORY_CACHE_TTL_MS = 30 * 60 * 1000
// A reputation payload alone runs ~19 KB, so the cache is capped by entry count and by total size.
const ARMORY_CACHE_LIMIT = 36
const ARMORY_CACHE_MAX_BYTES = 400000
const ARMORY_REQUEST_TIMEOUT_MS = 10000

const memoryCache = new Map()
const inFlightRequests = new Map()
let storageCache = null

function cacheKey(section, target) {
  return `${section}:${target.region}:${target.realm}:${target.name}`
}

function readStorage() {
  if (storageCache) return storageCache

  try {
    const raw = localStorage.getItem(ARMORY_STORAGE_KEY)
    const parsed = raw ? JSON.parse(raw) : {}
    storageCache = parsed && typeof parsed === 'object' ? parsed : {}
  } catch (error) {
    storageCache = {}
  }

  return storageCache
}

function writeStorage(entries) {
  storageCache = entries

  try {
    localStorage.setItem(ARMORY_STORAGE_KEY, JSON.stringify(entries))
  } catch (error) {
    // Quota is shared with the favorites list, which is far more valuable than this cache:
    // drop everything rather than let a full cache break persistence for the rest of the app.
    storageCache = {}
    try {
      localStorage.removeItem(ARMORY_STORAGE_KEY)
    } catch (removeError) {
      console.warn('Unable to reset the armory cache', removeError)
    }
  }
}

function trimEntries(entries) {
  const sorted = Object.entries(entries).sort(([, left], [, right]) => (right.storedAt || 0) - (left.storedAt || 0))
  const kept = {}
  let bytes = 0

  sorted.forEach(([key, entry], index) => {
    if (index >= ARMORY_CACHE_LIMIT) return
    bytes += JSON.stringify(entry).length
    if (bytes > ARMORY_CACHE_MAX_BYTES) return
    kept[key] = entry
  })

  return kept
}

function readCachedSection(key) {
  const fromMemory = memoryCache.get(key)
  if (fromMemory) return fromMemory

  const entry = readStorage()[key]
  if (!entry || entry.schemaVersion !== ARMORY_SCHEMA_VERSION) return null
  if (Date.now() - (entry.storedAt || 0) > ARMORY_CACHE_TTL_MS) return null

  memoryCache.set(key, entry.data)
  return entry.data
}

function writeCachedSection(key, data) {
  memoryCache.set(key, data)

  const entries = {
    ...readStorage(),
    [key]: { schemaVersion: ARMORY_SCHEMA_VERSION, storedAt: Date.now(), data },
  }

  writeStorage(trimEntries(entries))
}

function armorySectionUrl(section, target) {
  const url = new URL(`/armory/${section}`, ARMORY_WORKER_URL)
  url.searchParams.set('region', target.region)
  url.searchParams.set('realm', target.realm)
  url.searchParams.set('name', target.name)
  return url.toString()
}

async function requestSection(section, target) {
  const controller = new AbortController()
  const timeoutId = window.setTimeout(() => controller.abort(), ARMORY_REQUEST_TIMEOUT_MS)

  try {
    // `no-store` keeps the browser out of the way: freshness is owned by the worker's edge cache
    // and by this module's own layers.
    const response = await fetch(armorySectionUrl(section, target), {
      cache: 'no-store',
      signal: controller.signal,
    })

    if (!response.ok) {
      const details = await response.json().catch(() => ({}))
      const error = new Error(details.error || `Armory request failed: ${response.status}`)
      error.status = response.status
      throw error
    }

    const payload = await response.json()

    if (payload.schemaVersion !== ARMORY_SCHEMA_VERSION) {
      throw new Error('Armory data uses an unexpected schema version')
    }

    return payload
  } finally {
    window.clearTimeout(timeoutId)
  }
}

export function isArmorySection(section) {
  return ARMORY_SECTIONS.includes(section)
}

// Data older than the TTL is still rendered (the armory itself can be down for a while); the
// caller decides how to surface the age using `fetchedAt`.
export function isArmoryDataStale(payload) {
  const fetchedAt = Date.parse(payload?.fetchedAt || '')
  if (Number.isNaN(fetchedAt)) return false
  return Date.now() - fetchedAt > ARMORY_CACHE_TTL_MS
}

export function getArmorySection(section, profile, { force = false } = {}) {
  if (!isArmorySection(section)) {
    return Promise.reject(new Error(`Unknown armory section: ${section}`))
  }

  const target = characterPath(profile)

  if (!target.region || !target.realm || !target.name) {
    return Promise.reject(new Error('Character is missing region, realm or name'))
  }

  const key = cacheKey(section, target)

  if (!force) {
    const cached = readCachedSection(key)
    if (cached) return Promise.resolve(cached)

    const pending = inFlightRequests.get(key)
    if (pending) return pending
  }

  const request = requestSection(section, target)
    .then((payload) => {
      writeCachedSection(key, payload)
      return payload
    })
    .finally(() => {
      inFlightRequests.delete(key)
    })

  inFlightRequests.set(key, request)
  return request
}

export function clearArmoryCache() {
  memoryCache.clear()
  inFlightRequests.clear()
  storageCache = {}

  try {
    localStorage.removeItem(ARMORY_STORAGE_KEY)
  } catch (error) {
    console.warn('Unable to clear the armory cache', error)
  }
}
